import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";

// Icons
import editIcon from "../../assets/icons/edit.svg";
import trashIcon from "../../assets/icons/trash.svg";

// Actions
import {
  deletePhysicalDamage,
  getPhysicalDamages,
  updatePhysicalDamage,
  uploadDescriptions,
  uploadEstimatedCost
} from "../../state/physicalDamage/actions";
import { getCaseDetail } from "../../state/caseDetail/actions";
import { toggleSnack } from "../../state/snackbar/actions";

// Config
import { getRoute, urls } from "../../config/routes";
import { snackbarTypes } from "../../config/snackbar";
import styles from "../../config/styles";
import isCaseLocked from "../../utils/isCaseLocked";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import usePhysicalDamage from "../../hooks/usePhysicalDamage";
import useTranslate from "../../hooks/useTranslate";

// Components
import ConfirmationButton from "../ConfirmationButton";
import ContentCardHeader from "../ContentCardHeader";
import DetailValue from "../DetailValue";
import LoadingSection from "../LoadingSection";
import PhysicalDamageImages from "./PhysicalDamageImages";
import Text from "../Text";

// Sends null instead of "" for the sibling field, mirroring iOS.
const toApiValue = value => (value.trim() === "" ? null : value);

const useStyles = makeStyles(theme => ({
  data: {
    border: styles.values.border
  },
  title: {
    fontSize: 24
  },
  iconButton: {
    padding: 0,
    height: 40,
    width: 40,
    marginLeft: theme.spacing(1.5)
  },
  content: {
    padding: theme.spacing(4, 3.5)
  }
}));

const PhysicalDamageDetailPanel = ({ caseId, physicalDamageId }) => {
  const { data: caseDetail } = useSelector(s => s.caseDetail);
  const caseLocked = isCaseLocked(caseDetail);
  const { list, isUploadingDescription, isUploadingEstimatedCost } =
    useSelector(s => s.physicalDamage);
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const getText = useTranslate();

  const { data, isDeletingPhysicalDamage, error, initialized } =
    usePhysicalDamage(caseId, physicalDamageId);

  // Fetched only to compute this damage's position in the list ("Physical
  // Damage N" in the title) - the detail data itself comes from
  // usePhysicalDamage above.
  useEffect(() => {
    if (!list) dispatch(getPhysicalDamages({ caseId }));
  }, [caseId, dispatch, list]);

  const isCurrentPhysicalDamageLoaded = data && data.id === physicalDamageId;

  useEffect(() => {
    if (!isCurrentPhysicalDamageLoaded && initialized && error) {
      history.push(getRoute(urls.caseDetail, { caseId }));
    }
  }, [caseId, error, history, initialized, isCurrentPhysicalDamageLoaded]);

  if (!isCurrentPhysicalDamageLoaded) {
    return (
      <Paper elevation={0} className={classes.data}>
        <LoadingSection />
      </Paper>
    );
  }

  const index = list
    ? list.findIndex(item => item.id === physicalDamageId)
    : -1;
  const displayNumber = index >= 0 ? index + 1 : "";

  const descriptions = data.descriptions || [];
  const estimatedCosts = data.estimatedCosts || [];
  const currentDescription = (descriptions[0] && descriptions[0].text) || "";
  const currentEstimatedCost =
    (estimatedCosts[0] && estimatedCosts[0].text) || "";

  const handleDeletePhysicalDamage = () => {
    dispatch(
      deletePhysicalDamage({
        caseId,
        physicalDamageId,
        onFinish: success => {
          if (success) history.push(getRoute(urls.caseDetail, { caseId }));
          else {
            trackException(
              `Failed to delete physical damage: ${physicalDamageId} at case: ${caseId}`
            );
            // Refetches in case the rejection happened because the case
            // became locked mid-edit, so the UI picks up the current state.
            dispatch(getCaseDetail({ caseId, isUpdate: true }));
            dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
          }
        }
      })
    );
  };

  // Refetches in case the rejection happened because the case became locked
  // mid-edit, so the UI picks up the current state - DetailValue only tracks
  // the failure via telemetry, it doesn't show the user anything on its own.
  const onMutationFail = onFinish => success => {
    if (!success) {
      dispatch(getCaseDetail({ caseId, isUpdate: true }));
      dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
    }
    onFinish(success);
  };

  const handleUpdate = ({ form, onFinish }) =>
    dispatch(
      updatePhysicalDamage({
        caseId,
        physicalDamageId,
        form,
        onFinish: onMutationFail(onFinish)
      })
    );

  const handleAddDescription = ({ texts, onFinish }) =>
    dispatch(
      uploadDescriptions({
        caseId,
        physicalDamageId,
        texts,
        onFinish: onMutationFail(onFinish)
      })
    );

  const handleAddEstimatedCost = ({ texts, onFinish }) =>
    dispatch(
      uploadEstimatedCost({
        caseId,
        physicalDamageId,
        texts,
        onFinish: onMutationFail(onFinish)
      })
    );

  const buildUpdateForm = name => value => ({
    description: name === "description" ? value : toApiValue(currentDescription),
    estimatedCost:
      name === "estimatedCost" ? value : toApiValue(currentEstimatedCost)
  });

  return (
    <Paper elevation={0} className={classes.data}>
      <ContentCardHeader>
        <Text type="heading2" className={classes.title}>
          {`${getText("caseMenu.caseSummary.physicalDamage.title")} ${displayNumber}`}
        </Text>
        <div>
          {!caseLocked && (
            <IconButton
              className={classes.iconButton}
              onClick={() =>
                history.push(
                  getRoute(urls.editPhysicalDamage, { caseId, physicalDamageId })
                )
              }
            >
              <img src={editIcon} alt="" />
            </IconButton>
          )}
          {!caseLocked && (
            <ConfirmationButton
              className={classes.iconButton}
              type="icon"
              onClick={handleDeletePhysicalDamage}
              dialog={{
                isLoading: isDeletingPhysicalDamage,
                texts: [
                  {
                    key: 1,
                    text: getText("injuryDetailPage.deleteButtonDialog.title")
                  },
                  {
                    key: 2,
                    text: getText(
                      "injuryDetailPage.deleteButtonDialog.description"
                    )
                  }
                ]
              }}
            >
              <img src={trashIcon} alt="" />
            </ConfirmationButton>
          )}
        </div>
      </ContentCardHeader>
      <div className={classes.content}>
        <DetailValue
          addButtonText={getText("editInjuryPage.descriptionAddButton")}
          buildUpdateForm={buildUpdateForm("description")}
          caseId={caseId}
          itemId={physicalDamageId}
          canAddMore={!caseLocked}
          content={descriptions}
          isDraft={caseDetail.isDraft}
          isUpdating={isUploadingEstimatedCost}
          isUploadingAdd={isUploadingDescription}
          name="description"
          onAdd={handleAddDescription}
          onUpdate={handleUpdate}
          title={getText("editPhysicalDamagePage.damageDescriptionTitle")}
        />
        <DetailValue
          addButtonText={getText("editInjuryPage.descriptionAddButton")}
          buildUpdateForm={buildUpdateForm("estimatedCost")}
          caseId={caseId}
          itemId={physicalDamageId}
          canAddMore={!caseLocked}
          content={estimatedCosts}
          isDraft={caseDetail.isDraft}
          isUpdating={isUploadingEstimatedCost}
          isUploadingAdd={isUploadingEstimatedCost}
          name="estimatedCost"
          onAdd={handleAddEstimatedCost}
          onUpdate={handleUpdate}
          title={getText("editPhysicalDamagePage.costEstimateInputTitle")}
        />
        <PhysicalDamageImages
          caseId={caseId}
          physicalDamageId={physicalDamageId}
          images={data.images || []}
        />
      </div>
    </Paper>
  );
};

PhysicalDamageDetailPanel.propTypes = {
  caseId: PropTypes.string.isRequired,
  physicalDamageId: PropTypes.string.isRequired
};

export default PhysicalDamageDetailPanel;
